import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar, Mail, Phone, Users, MapPin, CheckCircle, FileText, DollarSign, Trash2, Search, Filter } from "lucide-react";
import { toast } from "sonner";

const ViewBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [zelleLink, setZelleLink] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/bookings`, { headers });
      setBookings(response.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptBooking = async (bookingId) => {
    try {
      const token = localStorage.getItem("adminToken");
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/bookings/${bookingId}/accept`, {}, { headers });
      toast.success("Booking accepted! Invoice generated.");
      fetchBookings();
    } catch (error) {
      console.error("Error accepting booking:", error);
      toast.error("Failed to accept booking");
    }
  };

  const handleAddPaymentLink = async () => {
    if (!selectedBooking || !zelleLink) {
      toast.error("Please enter a Zelle payment link");
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API}/bookings/${selectedBooking.id}/payment`,
        { zelle_payment_link: zelleLink },
        { headers }
      );
      toast.success("Zelle payment link added successfully!");
      setIsDialogOpen(false);
      setZelleLink("");
      fetchBookings();
    } catch (error) {
      console.error("Error adding payment link:", error);
      toast.error("Failed to add payment link");
    }
  };

  const generateInvoicePDF = (booking) => {
    // Simple invoice generation - in production, you'd use a proper PDF library
    const invoiceContent = `
INVOICE
${booking.invoice_number || 'N/A'}
Date: ${new Date().toLocaleDateString()}

Bill To:
${booking.name}
${booking.email}
${booking.phone}
${booking.address}

Event Details:
Event Date: ${booking.event_date}
Event Type: ${booking.event_type}
Guest Count: ${booking.guest_count}
${booking.message ? `Notes: ${booking.message}` : ''}

Status: ${booking.status}
${booking.zelle_payment_link ? `Payment Link: ${booking.zelle_payment_link}` : ''}

---
Thank you for choosing Mélange Mobile Bar!
    `;

    const blob = new Blob([invoiceContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Invoice_${booking.invoice_number || booking.id}.txt`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Invoice downloaded!");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to delete this booking? This action cannot be undone.")) {
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/bookings/${bookingId}`, { headers });
      toast.success("Booking deleted successfully!");
      fetchBookings();
    } catch (error) {
      console.error("Error deleting booking:", error);
      toast.error("Failed to delete booking");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Filter bookings based on search query and status
  const filteredBookings = bookings.filter(booking => {
    // Status filter
    if (statusFilter !== "all" && booking.status !== statusFilter) {
      return false;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        booking.name.toLowerCase().includes(query) ||
        booking.email.toLowerCase().includes(query) ||
        booking.phone.toLowerCase().includes(query) ||
        booking.address.toLowerCase().includes(query) ||
        booking.event_type.toLowerCase().includes(query) ||
        booking.event_date.includes(query) ||
        (booking.invoice_number && booking.invoice_number.toLowerCase().includes(query)) ||
        (booking.message && booking.message.toLowerCase().includes(query))
      );
    }

    return true;
  });

  // Group bookings by event date
  const groupedBookings = filteredBookings.reduce((groups, booking) => {
    const date = booking.event_date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(booking);
    return groups;
  }, {});

  const sortedDates = Object.keys(groupedBookings).sort((a, b) => new Date(b) - new Date(a));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B7355] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="view-bookings">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Bookings & Quotes</h1>
        <div className="text-sm text-gray-600">
          {filteredBookings.length} of {bookings.length} bookings
        </div>
      </div>

      {/* Search and Filter Section */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder="Search by name, email, phone, address, event type, date, or invoice..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="booking-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="clear-search"
                >
                  ×
                </button>
              )}
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="text-gray-400" size={20} />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Active Filters Display */}
          {(searchQuery || statusFilter !== "all") && (
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-600">Active filters:</span>
              {searchQuery && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  Search: "{searchQuery}"
                  <button
                    onClick={() => setSearchQuery("")}
                    className="ml-1 hover:text-gray-700"
                  >
                    ×
                  </button>
                </Badge>
              )}
              {statusFilter !== "all" && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  Status: {statusFilter}
                  <button
                    onClick={() => setStatusFilter("all")}
                    className="ml-1 hover:text-gray-700"
                  >
                    ×
                  </button>
                </Badge>
              )}
              <button
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter("all");
                }}
                className="text-sm text-[#8B7355] hover:underline"
                data-testid="clear-all-filters"
              >
                Clear all
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {sortedDates.length > 0 ? (
        sortedDates.map((date) => (
        <div key={date} className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <Calendar className="mr-2 text-[#8B7355]" size={20} />
            {formatDate(date)}
          </h2>
          <div className="space-y-4">
            {groupedBookings[date].map((booking, index) => (
              <Card key={booking.id} data-testid={`booking-card-${index}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl">{booking.name}</CardTitle>
                      {booking.invoice_number && (
                        <p className="text-sm text-gray-600 mt-1">
                          Invoice: {booking.invoice_number}
                        </p>
                      )}
                    </div>
                    <Badge className={getStatusColor(booking.status)}>
                      {booking.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="space-y-2">
                      <div className="flex items-center text-gray-700">
                        <Mail size={16} className="mr-2" />
                        <span className="text-sm">{booking.email}</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <Phone size={16} className="mr-2" />
                        <span className="text-sm">{booking.phone}</span>
                      </div>
                      <div className="flex items-start text-gray-700">
                        <MapPin size={16} className="mr-2 mt-1 flex-shrink-0" />
                        <span className="text-sm">{booking.address}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center text-gray-700">
                        <Users size={16} className="mr-2" />
                        <span className="text-sm">{booking.guest_count} guests</span>
                      </div>
                      <div className="text-sm text-gray-700">
                        <strong>Event Type:</strong> {booking.event_type}
                      </div>
                      {booking.message && (
                        <div className="text-sm text-gray-700">
                          <strong>Message:</strong> {booking.message}
                        </div>
                      )}
                      {booking.zelle_payment_link && (
                        <div className="text-sm text-gray-700">
                          <strong>Payment Link:</strong>{" "}
                          <a
                            href={booking.zelle_payment_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#8B7355] hover:underline"
                          >
                            View Zelle Link
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2 pt-4 border-t">
                    {booking.status === "pending" && (
                      <Button
                        onClick={() => handleAcceptBooking(booking.id)}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`accept-booking-${index}`}
                      >
                        <CheckCircle size={16} className="mr-2" />
                        Accept Booking
                      </Button>
                    )}

                    {booking.status === "confirmed" && booking.invoice_number && (
                      <Button
                        onClick={() => generateInvoicePDF(booking)}
                        size="sm"
                        variant="outline"
                        data-testid={`generate-invoice-${index}`}
                      >
                        <FileText size={16} className="mr-2" />
                        Download Invoice
                      </Button>
                    )}

                    <Button
                      onClick={() => handleDeleteBooking(booking.id)}
                      size="sm"
                      variant="destructive"
                      data-testid={`delete-booking-${index}`}
                    >
                      <Trash2 size={16} className="mr-2" />
                      Delete
                    </Button>

                    <Dialog open={isDialogOpen && selectedBooking?.id === booking.id} onOpenChange={(open) => {
                      setIsDialogOpen(open);
                      if (open) setSelectedBooking(booking);
                    }}>
                      <DialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white"
                          data-testid={`add-zelle-${index}`}
                        >
                          <DollarSign size={16} className="mr-2" />
                          {booking.zelle_payment_link ? "Update" : "Add"} Zelle Link
                        </Button>
                      </DialogTrigger>
                      <DialogContent data-testid="zelle-dialog">
                        <DialogHeader>
                          <DialogTitle>Add Zelle Payment Link</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="zelle-link">Zelle Payment Link</Label>
                            <Input
                              id="zelle-link"
                              placeholder="https://zellepay.com/..."
                              value={zelleLink}
                              onChange={(e) => setZelleLink(e.target.value)}
                              data-testid="zelle-link-input"
                            />
                          </div>
                          <Button
                            onClick={handleAddPaymentLink}
                            className="w-full"
                            data-testid="save-zelle-link"
                          >
                            Save Payment Link
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))) : (
        <div className="text-center py-12">
          <Search className="mx-auto mb-4 text-gray-400" size={48} />
          <p className="text-gray-600 mb-2">
            {bookings.length === 0
              ? "No bookings yet."
              : "No bookings match your search criteria."}
          </p>
          {(searchQuery || statusFilter !== "all") && (
            <button
              onClick={() => {
                setSearchQuery("");
                setStatusFilter("all");
              }}
              className="text-[#8B7355] hover:underline text-sm"
            >
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ViewBookings;
